// src/js/utils/chatThread.ts
// Phase 251 (251-08) — dependency-free clustering, coverage-state and dedup logic for the
// `/mc/[server]` DM-style chat thread.
//
// This module exists because `src/js/mc/chatlog.ts` builds real page elements and issues
// network calls (an mc-heads.net avatar image, an HTTP read of the thread), so
// `node tests/mc/*.test.ts` could never load it without a bundler and a browser-DOM shim this
// project deliberately does not carry. Every decision worth pinning — cluster boundaries, the
// three-state coverage matrix, and row/id dedup — is extracted here instead, exactly the way
// `src/js/utils/feedTail.ts` (Phase 243) and `src/js/utils/instants.ts` (Phase 240) were split
// out of their own page-touching modules before them. Every export below is pure: nothing here
// reads or writes any page global, issues a network call, or starts a timer/interval, and there
// is no `import` statement of any kind.
//
// Implements 251-UI-SPEC.md §8.3 (5-minute grouping span + its splitting rules), §8.8 (the
// three-state opt-in/logging-since/not-logging matrix) and §8.9 (null-content rows are
// silently omitted, never placeholdered).

// ---------------------------------------------------------------------------------------
// Exported constants
// ---------------------------------------------------------------------------------------

/** UI-SPEC §8.3's locked 5-minute grouping span — a middle ground between Discord's 7-minute
 *  default and the tighter same-minute grouping some DM apps use, chosen for a small
 *  conversational fleet where chat bursts are conversational, not high-frequency. Tunable
 *  without affecting anything else in this contract — nothing downstream derives a value from
 *  this constant's specific number, only from the comparison against it. */
export const GROUPING_WINDOW_MS = 5 * 60 * 1000;

/** Must mirror `McChatModel::PAGE_SIZE` (app/Models/McChatModel.php) — a future change to one
 *  side without the other is a silent page-size mismatch between server and client. Exactly the
 *  same mirrored-constant discipline `FEED_PAGE_LIMIT` documents for
 *  `McFeedModel::getActivityStream()`'s own default in `feedTail.ts`. */
export const CHAT_PAGE_LIMIT = 50;

/** Bound on the seen-`row_key` `Set` for a long-lived tab — oldest-first eviction happens in
 *  `recordSeenKeys()` below, mirroring `feedTail.ts`'s `recordSeenIds()` eviction shape. */
export const DEDUP_KEY_CAP = 1000;

/** Bound on in-DOM chat bubbles. Plan 251-09 owns the growth paths (Load older / live tail)
 *  that actually enforce this cap against real DOM node counts; it is declared here so both
 *  plans share one source of truth for the number. */
export const THREAD_ROW_CAP = 500;

// ---------------------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------------------

/**
 * Phase 274 (D-07/CHAT-01) — a deliberate STRUCTURAL DUPLICATE of `src/js/mc/coordPill.ts`'s
 * exported `NarrativeSegment` union. `src/js/mc/coordPill.ts` is the authoritative definition;
 * this is not a second implementation, it exists ONLY because this file's own docblock forbids
 * any `import` statement (so `node tests/mc/*.test.ts` can load it with no bundler). TypeScript's
 * structural typing is what lets a `ChatRowLike.narrative_segments` value be passed straight into
 * `coordPill.ts`'s `appendNarrativeSegments()` with no cast — which only works as long as the two
 * unions stay field-for-field identical. A change to `coordPill.ts`'s `NarrativeSegment` union
 * REQUIRES the matching change here, in the SAME commit — exactly the lock-step obligation
 * `src/js/utils/mcKeys.ts` and `app/Mc/McHumanize.php` already state for each other.
 */
export type ChatNarrativeSegment =
    | { type: 'text'; value: string }
    | { type: 'coord'; x: number; y: number; z: number; dimension: string; href: string | null };

/** The structural subset of `McChatModel::buildRow()`'s wire row shape this module needs. */
export interface ChatRowLike {
    row_key: string;
    kind: 'player' | 'say';
    occurred_at: string;
    occurred_at_ms: number;
    player_uuid: string | null;
    player_name: string | null;
    content: string;
    // Phase 274 (D-07/CHAT-01): the ordered text/coord segment array `McChatModel::buildRow()`
    // emits only for a row whose content parsed as a Xaero waypoint. Optional because a row can
    // arrive from a pre-274 backend during a deploy window — `content` above is the fallback the
    // client renders when these three fields are absent. Mirrors `src/js/mc/feed.ts`'s own
    // `StreamRow.narrative_segments?` field convention.
    narrative_segments?: ChatNarrativeSegment[];
    // Phase 274 (D-04): a plain-language sentence WITHOUT the sender prefix, for the bubble's
    // `aria-label`. Optional for the same deploy-window reason as `narrative_segments` above.
    waypoint_aria?: string;
    // Phase 274 (D-03): the raw `xaero-waypoint:...` substring, for the bubble's `title`
    // attribute. Optional for the same deploy-window reason as `narrative_segments` above.
    waypoint_raw?: string;
}

/** The structural subset of `McChatModel::getCoverage()`'s wire shape this module needs. */
export interface ChatCoverageLike {
    earliest: string | null;
    earliest_ms: number | null;
    complete: boolean;
}

/** One grouped sender run — UI-SPEC §8.3's "avatar/name shown once, bubbles stack below" unit. */
export interface ChatCluster {
    kind: 'player' | 'say';
    player_uuid: string | null;
    player_name: string | null;
    rows: ChatRowLike[];
}

/** UI-SPEC §8.8's three mutually-exclusive states. */
export type CoverageState = 'never-logged' | 'since-boundary' | 'complete';

// ---------------------------------------------------------------------------------------
// Exported functions
// ---------------------------------------------------------------------------------------

/**
 * The identity two rows must share to belong to the same cluster. `say` rows all share the
 * constant `'say'` — there is exactly one operator in this thread (UI-SPEC §8.3: outgoing
 * clusters never show a name/avatar, right-alignment alone identifies them). `player` rows use
 * the uuid when present, otherwise fall back to `'name:' + (player_name ?? '')` — the fallback
 * is what lets two unresolved backfill rows (D-19, `player_uuid === null`) from the same
 * username group together; using only the uuid would leave every unresolved row in its own
 * single-message cluster forever.
 */
export function senderKey(row: ChatRowLike): string {
    if (row.kind === 'say') {
        return 'say';
    }
    return row.player_uuid !== null ? row.player_uuid : 'name:' + (row.player_name ?? '');
}

/**
 * True when `prev` is null (first row ever), when `senderKey()` differs between `prev` and
 * `next`, or when the gap between them exceeds `GROUPING_WINDOW_MS`. The gap comparison is
 * strictly `>`, not `>=`, so a message landing exactly at that threshold still groups with
 * its predecessor — the boundary instant itself belongs to the SAME cluster.
 */
export function startsNewCluster(prev: ChatRowLike | null, next: ChatRowLike): boolean {
    if (prev === null) return true;
    if (senderKey(prev) !== senderKey(next)) return true;
    return Math.abs(next.occurred_at_ms - prev.occurred_at_ms) > GROUPING_WINDOW_MS;
}

/**
 * Folds `rows` into clusters via `startsNewCluster()`.
 *
 * IMPORTANT: `rows` must already be in OLDEST-FIRST display order. The wire delivers rows
 * newest-first (`McChatModel::fetchMerged()`'s `ORDER BY occurred_at DESC`); the caller MUST
 * reverse that order before calling this function. Feeding a newest-first array here would
 * silently produce plausible-LOOKING but chronologically-backwards, incorrect clusters — this
 * function has no way to detect that its input order is wrong, so getting the order right is
 * entirely the caller's responsibility.
 */
export function clusterRows(rows: ChatRowLike[]): ChatCluster[] {
    const clusters: ChatCluster[] = [];
    let prev: ChatRowLike | null = null;
    let current: ChatCluster | null = null;

    for (const row of rows) {
        if (startsNewCluster(prev, row)) {
            current = {
                kind: row.kind,
                player_uuid: row.player_uuid,
                player_name: row.player_name,
                rows: [],
            };
            clusters.push(current);
        }
        // `current` is always non-null here: the loop's first iteration always takes the
        // startsNewCluster branch above (prev === null → true), so `current` is assigned
        // before this line can ever run.
        (current as ChatCluster).rows.push(row);
        prev = row;
    }

    return clusters;
}

/**
 * UI-SPEC §8.8's three-state selection. `complete` is checked FIRST so the three states stay
 * mutually exclusive by construction — a coverage payload with both `complete: true` and a
 * non-null `earliest` (a shape the backend should never emit, but this function must still
 * resolve deterministically) always yields `'complete'`, never a state where two notes could
 * both render.
 */
export function coverageState(coverage: ChatCoverageLike): CoverageState {
    if (coverage.complete === true) return 'complete';
    if (coverage.earliest === null) return 'never-logged';
    return 'since-boundary';
}

/**
 * Filters `rows` down to those whose `row_key` is not already in `seen`, ALSO deduplicating
 * within `rows` itself — a `row_key` repeated twice inside one payload must survive only once.
 * Does not mutate `seen`; the caller records the surviving keys via `recordSeenKeys()`
 * afterward. Mirrors `feedTail.ts`'s `selectUnseenRows()` shape, keyed on `row_key` instead of
 * `id`.
 */
export function selectUnseenRows(rows: ChatRowLike[], seen: Set<string>): ChatRowLike[] {
    const emittedThisCall = new Set<string>();
    const result: ChatRowLike[] = [];
    for (const row of rows) {
        if (seen.has(row.row_key) || emittedThisCall.has(row.row_key)) {
            continue;
        }
        emittedThisCall.add(row.row_key);
        result.push(row);
    }
    return result;
}

/**
 * Inserts every key into `seen`, then evicts oldest-first (a `Set` iterates in insertion order,
 * so `seen.values().next().value` is always the oldest surviving entry) while the set exceeds
 * `cap`. Mirrors `feedTail.ts`'s `recordSeenIds()` eviction shape exactly.
 */
export function recordSeenKeys(keys: string[], seen: Set<string>, cap: number = DEDUP_KEY_CAP): void {
    for (const key of keys) {
        seen.add(key);
    }
    while (seen.size > cap) {
        const oldest = seen.values().next().value;
        if (oldest === undefined) break;
        seen.delete(oldest);
    }
}

/**
 * The short date used in the "Chat logging since {date}" divider (UI-SPEC §8.8 state B).
 * Reuses `resourceHistory.ts`'s own `formatCoverageDate()` short-date formatting call verbatim
 * (day + short month, `en-CA` locale) — this codebase does not invent a second date style for
 * this one surface.
 */
export function formatCoverageDate(ms: number): string {
    return new Intl.DateTimeFormat('en-CA', { day: 'numeric', month: 'short' }).format(new Date(ms));
}

// ---------------------------------------------------------------------------------------
// 251-09 additions — Load-older scroll preservation, live-tail baseline arithmetic and the
// "new messages" pill's count copy. Still zero-import, still pure: nothing below reads or
// writes a page global, issues a network call, or starts a timer.
// ---------------------------------------------------------------------------------------

/**
 * UI-SPEC §8.7's near-bottom threshold: true when the reader is within 10px of the bottom
 * edge of the scroll container. The literal `10` here is deliberately the SAME value
 * `console.ts`'s own `isAtBottom()` expression uses (`scrollHeight - scrollTop - clientHeight
 * < 10`) — UI-SPEC §8.7 names that exact threshold as the one to reuse, not an independently
 * chosen number for this surface.
 */
export function shouldAutoFollow(scrollHeight: number, scrollTop: number, clientHeight: number): boolean {
    return scrollHeight - scrollTop - clientHeight < 10;
}

/**
 * The prepend scroll-position compensation for "Load older messages" (UI-SPEC §8.7). After
 * inserting older rows ABOVE the current top of the thread, the reader's visible position must
 * not jump — the container's `scrollTop` has to grow by exactly the height the prepend just
 * added. `Math.max(0, …)` guards the (should-never-happen) case where the container somehow
 * reports a smaller height after an insertion, so this can never return a value below
 * `prevScrollTop`.
 */
export function preservedScrollTop(prevScrollHeight: number, prevScrollTop: number, nextScrollHeight: number): number {
    return prevScrollTop + Math.max(0, nextScrollHeight - prevScrollHeight);
}

/**
 * The live-tail `since` query-parameter value. Mirrors `feedTail.ts`'s own `tailSinceParam()`
 * shape exactly: `null` passthrough when there is no baseline instant yet (a normal state before
 * any tail tick has ever recorded one), otherwise the ISO 8601 instant string the backend's
 * `since` parameter expects. The backend boundary is INCLUSIVE (`>=`), so the caller must dedup
 * by `row_key` — the same watermark second can legitimately come back on the very next tick.
 */
export function tailSinceIso(newestOccurredAtMs: number | null): string | null {
    if (newestOccurredAtMs === null || !Number.isFinite(newestOccurredAtMs)) {
        return null;
    }
    return new Date(newestOccurredAtMs).toISOString();
}

/**
 * The "new messages" pill's count copy (UI-SPEC §8.7) — singular for exactly one, plural
 * otherwise. Mirrors `feedTail.ts`'s own `arrivalLabel()` shape and copy style.
 */
export function pillLabel(count: number): string {
    return count === 1 ? '1 new message' : `${count} new messages`;
}

// ---------------------------------------------------------------------------------------
// Day dividers (post-251-09 checkpoint polish — operator ask: "center headings for when
// it's a new day, like in DMs"). Day boundaries are a display concern, grouped by the
// VIEWER's own local calendar day — the same client-local convention localizeInstant()
// already applies to every individual timestamp in this thread — never the DB's UTC day.
// ---------------------------------------------------------------------------------------

/**
 * The viewer's local calendar-day key for an instant, e.g. "2026-08-07". Two instants that
 * fall on the same local day (regardless of hour) share this key.
 */
export function localDayKey(ms: number): string {
    const d = new Date(ms);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * True when `nextMs` falls on a different local calendar day than `prevMs`, or when `prevMs`
 * is null — the very first cluster rendered always opens with a day divider.
 */
export function isNewLocalDay(prevMs: number | null, nextMs: number): boolean {
    return prevMs === null || localDayKey(prevMs) !== localDayKey(nextMs);
}

/**
 * "Today" / "Yesterday" / a short weekday+date label for anything older — the DM-app
 * convention for a day-boundary heading. `nowMs` is an explicit parameter, never read from
 * `Date.now()` internally, so this stays a pure, node-testable function; the caller supplies
 * the real current instant at render time. Yesterday is computed via calendar-day arithmetic
 * (`Date`'s own day-rollover), not a naive `-24h` offset, so it stays correct across a DST
 * transition.
 */
export function localDayLabel(ms: number, nowMs: number): string {
    const key = localDayKey(ms);
    if (key === localDayKey(nowMs)) return 'Today';

    const now = new Date(nowMs);
    const yesterdayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    if (key === localDayKey(yesterdayMs)) return 'Yesterday';

    return new Intl.DateTimeFormat('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }).format(
        new Date(ms),
    );
}
